import React, { useState, useRef, useEffect } from 'react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';

interface SavePinPopupProps {
  lat: number;
  lng: number;
  onSave: (name: string, note: string | null) => Promise<void>;
  onCancel: () => void;
}

export const SavePinPopup: React.FC<SavePinPopupProps> = ({ lat, lng, onSave, onCancel }) => {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Synchronous in-flight guard — React state updates are batched, so two
  // rapid Enter keypresses would both pass a `saving === false` check.
  const inFlightRef = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSave = async () => {
    if (inFlightRef.current) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setErr('Name is required');
      return;
    }
    inFlightRef.current = true;
    setSaving(true);
    setErr(null);
    try {
      await onSave(trimmed, note.trim() || null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
      setSaving(false);
      inFlightRef.current = false;
    }
  };

  return (
    <div
      style={{
        minWidth: '220px',
        padding: '8px 4px 4px',
        fontFamily: designTokens.typography.fontFamily.display,
        color: theme.text,
      }}
    >
      <div
        style={{
          fontSize: '9px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: theme.textSecondary,
          marginBottom: '6px',
        }}
      >
        Save Location
      </div>
      <div
        style={{
          fontSize: '10px',
          fontFamily: designTokens.typography.fontFamily.mono,
          color: theme.textTertiary,
          marginBottom: '8px',
        }}
      >
        {lat.toFixed(5)}, {lng.toFixed(5)}
      </div>
      <input
        ref={inputRef}
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') onCancel();
        }}
        placeholder="Name this location"
        style={{
          width: '100%',
          padding: '6px 8px',
          fontSize: '12px',
          border: `1px solid ${theme.border}`,
          borderRadius: '3px',
          background: theme.inputBg,
          color: theme.text,
          marginBottom: '6px',
          boxSizing: 'border-box',
        }}
      />
      <textarea
        value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="Note (optional)"
        rows={2}
        style={{
          width: '100%',
          padding: '6px 8px',
          fontSize: '11px',
          border: `1px solid ${theme.border}`,
          borderRadius: '3px',
          background: theme.inputBg,
          color: theme.text,
          marginBottom: '6px',
          boxSizing: 'border-box',
          resize: 'vertical',
          fontFamily: designTokens.typography.fontFamily.display,
        }}
      />
      {err && (
        <div style={{ fontSize: '10px', color: '#dc2626', marginBottom: '6px' }}>{err}</div>
      )}
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '4px 10px',
            fontSize: '11px',
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: '3px',
            color: theme.text,
            cursor: 'pointer',
            fontFamily: designTokens.typography.fontFamily.display,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !name.trim()}
          style={{
            padding: '4px 10px',
            fontSize: '11px',
            background: designTokens.colors.brand,
            border: 'none',
            borderRadius: '3px',
            color: '#fff',
            cursor: saving || !name.trim() ? 'not-allowed' : 'pointer',
            opacity: saving || !name.trim() ? 0.6 : 1,
            fontFamily: designTokens.typography.fontFamily.display,
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};
